export type RouteHandler = (params: Record<string, string>) => void;

interface Route {
  path: string;
  pattern: RegExp;
  paramNames: string[];
  handler?: RouteHandler;
}

export interface RouteChangeEvent {
  path: string;
  params: Record<string, string>;
  query: Record<string, string>;
}

type RouteChangeCallback = (event: RouteChangeEvent) => void;

export class Router {
  private static instance: Router;
  private routes: Route[] = [];
  private listeners: Set<RouteChangeCallback> = new Set();
  private currentPath = '';

  private constructor() {
    window.addEventListener('popstate', () => this.handleRouteChange());
    this.currentPath = this.getPath();
  }

  static getInstance(): Router {
    if (!Router.instance) Router.instance = new Router();
    return Router.instance;
  }

  static register(path: string, handler?: RouteHandler): void {
    Router.getInstance().addRoute(path, handler);
  }

  static navigate(path: string, replace = false): void {
    Router.getInstance().navigateTo(path, replace);
  }

  static getCurrentPath(): string {
    return Router.getInstance().getPath();
  }

  static getParams(): Record<string, string> {
    return Router.getInstance().getCurrentParams();
  }

  static getQuery(): Record<string, string> {
    return Router.getInstance().getQueryParams();
  }

  static onChange(callback: RouteChangeCallback): () => void {
    return Router.getInstance().subscribe(callback);
  }

  static init(): void {
    Router.getInstance().handleRouteChange();
  }

  static match(pattern: string, path: string): Record<string, string> | null {
    const paramNames: string[] = [];
    const patternStr = pattern
      .replace(/\//g, '\\/')
      .replace(/:([a-zA-Z_]\w*)/g, (_, name) => { paramNames.push(name); return '([^/]+)'; })
      .replace(/\*/g, '.*');
    const regex = new RegExp(`^${patternStr}$`);
    const match = path.match(regex);
    if (!match) return null;
    const params: Record<string, string> = {};
    paramNames.forEach((n, i) => { params[n] = match[i + 1]; });
    return params;
  }

  private addRoute(path: string, handler?: RouteHandler): void {
    const paramNames: string[] = [];
    const patternStr = path
      .replace(/\//g, '\\/')
      .replace(/:([a-zA-Z_]\w*)/g, (_, name) => { paramNames.push(name); return '([^/]+)'; })
      .replace(/\*/g, '.*');
    this.routes.push({ path, pattern: new RegExp(`^${patternStr}$`), paramNames, handler });
  }

  private navigateTo(path: string, replace = false): void {
    const current = `${window.location.pathname}${window.location.search}`;
    if (!replace && current === path) return;
    if (replace) window.history.replaceState({}, '', path);
    else window.history.pushState({}, '', path);
    this.handleRouteChange();
  }

  private getPath(): string {
    return window.location.pathname;
  }

  private getCurrentParams(): Record<string, string> {
    const path = this.getPath();
    for (const route of this.routes) {
      const match = path.match(route.pattern);
      if (match) {
        const params: Record<string, string> = {};
        route.paramNames.forEach((n, i) => { params[n] = match[i + 1]; });
        return params;
      }
    }
    return {};
  }

  private getQueryParams(): Record<string, string> {
    const params: Record<string, string> = {};
    new URLSearchParams(window.location.search).forEach((v, k) => { params[k] = v; });
    return params;
  }

  private subscribe(callback: RouteChangeCallback): () => void {
    this.listeners.add(callback);
    return () => { this.listeners.delete(callback); };
  }

  private handleRouteChange(): void {
    const path = this.getPath();
    this.currentPath = path;
    const params = this.getCurrentParams();
    const query = this.getQueryParams();

    for (const route of this.routes) {
      const match = path.match(route.pattern);
      if (match) {
        const routeParams: Record<string, string> = {};
        route.paramNames.forEach((n, i) => { routeParams[n] = match[i + 1]; });
        route.handler?.(routeParams);
        break;
      }
    }

    const event: RouteChangeEvent = { path, params, query };
    this.listeners.forEach(cb => { try { cb(event); } catch (e) { console.error('Route change error:', e); } });
  }
}

export default Router;
